export type CachedCandidateFilters = {
  filters?: Record<string, unknown>;
  cities?: string[];
  distance_in_miles?: number | null;
  total_candidates?: number | null;
  has_saved_filters?: boolean;
  cache_available?: boolean;
};

export type GraphResponseMeta = {
  sliderCandidateCount: number | null;
  filterSearchTotalCandidates: number | null;
  selectedPercentageFit: number | null;
};

const advancedFiltersKey = (jobId: string) => `job:${jobId}:advancedFilters`;

/** Clear editable (active) filter state stored locally for a job. */
export const clearActiveCandidateFilters = (jobId: string): void => {
  if (!jobId) return;
  try {
    localStorage.removeItem(advancedFiltersKey(jobId));
  } catch {
    // ignore storage errors
  }
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const unwrapResponseBody = (raw: unknown): Record<string, unknown> => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const root = raw as Record<string, unknown>;
  const nested = root.data;

  // `{ data: [ points... ], selected_percentage_fit, ... }` — keep this object; do not
  // peel into the points array or meta fields are lost.
  if (Array.isArray(nested)) {
    return root;
  }

  // `{ detail, data: { data: [points], selected_percentage_fit, ... } }`
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }

  return root;
};

export type GraphSeriesPoint = {
  percentage_fit: number;
  number_of_candidates?: number;
  candidates?: string[];
};

/**
 * Normalize graph-generate / distance-matching payloads into points + meta body.
 * Supports both `{ data: [...] }` and `{ detail, data: { data: [...] } }`.
 */
export const extractGraphSeries = (
  raw: unknown,
): { points: GraphSeriesPoint[]; body: Record<string, unknown> } => {
  const body = unwrapResponseBody(raw);
  const points = Array.isArray(body.data) ? (body.data as GraphSeriesPoint[]) : [];
  return { points, body };
};

const APPLIED_FILTER_META_KEYS = new Set([
  'cities',
  'city',
  'distance_in_miles',
  'has_saved_filters',
  'cache_available',
  'job',
  'total_candidates',
  'filter_cache_current',
  'fingerprint',
  'graph_search_engine',
  'source',
  'filters',
]);

/** Build the nested `filters` object from an applied_filters / cached body. */
const filtersObjectFromAppliedBody = (applied: Record<string, unknown>): Record<string, unknown> => {
  const nested = applied.filters;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...(nested as Record<string, unknown>) };
  }

  const filters: Record<string, unknown> = {};
  Object.entries(applied).forEach(([key, value]) => {
    if (!APPLIED_FILTER_META_KEYS.has(key)) {
      filters[key] = value;
    }
  });
  return filters;
};

const pickAppliedBody = (body: Record<string, unknown>): Record<string, unknown> | null => {
  const applied =
    body.applied_filters ??
    (body.saved_filters as Record<string, unknown> | undefined)?.applied;

  if (applied && typeof applied === 'object' && !Array.isArray(applied)) {
    return applied as Record<string, unknown>;
  }

  const legacyCached = (body.saved_filters as Record<string, unknown> | undefined)?.cached;
  if (legacyCached && typeof legacyCached === 'object' && !Array.isArray(legacyCached)) {
    return legacyCached as Record<string, unknown>;
  }

  if (
    body.filters !== undefined ||
    body.cities !== undefined ||
    body.distance_in_miles !== undefined ||
    body.job_title_filters !== undefined ||
    body.certifications !== undefined
  ) {
    return body;
  }

  return null;
};

/**
 * Raw applied-filter slice for hydrating the advanced-filters modal.
 * Preserves nested keys (`job_title_filters`, `companies_worked_in`, etc.).
 */
export const extractRawAppliedFiltersSlice = (raw: unknown): {
  filters: Record<string, unknown>;
  cities: string[];
  distance_in_miles: number | null;
  has_saved_filters?: boolean;
  city?: string;
} => {
  const body = unwrapResponseBody(raw);
  const applied = pickAppliedBody(body);

  if (!applied) {
    return { filters: {}, cities: [], distance_in_miles: null };
  }

  const cities = Array.isArray(applied.cities)
    ? (applied.cities as string[])
    : applied.city
      ? [String(applied.city)]
      : [];

  return {
    filters: filtersObjectFromAppliedBody(applied),
    cities,
    distance_in_miles: toNumberOrNull(applied.distance_in_miles),
    has_saved_filters: applied.has_saved_filters !== undefined ? !!applied.has_saved_filters : undefined,
    city: typeof applied.city === 'string' ? applied.city : undefined,
  };
};

/** Normalize a filters/cities/distance payload for UI and generate-candidate. */
export const normalizeCachedFiltersResponse = (raw: unknown): CachedCandidateFilters => {
  const slice = extractRawAppliedFiltersSlice(raw);
  const body = unwrapResponseBody(raw);
  const applied = pickAppliedBody(body) ?? body;

  return {
    filters: slice.filters,
    cities: slice.cities,
    distance_in_miles: slice.distance_in_miles,
    total_candidates: toNumberOrNull(applied.total_candidates ?? body.total_candidates),
    has_saved_filters:
      slice.has_saved_filters ??
      (applied.has_saved_filters !== undefined ? !!applied.has_saved_filters : !!body.has_saved_filters),
    cache_available: !!(applied.cache_available ?? body.cache_available),
  };
};

/**
 * Extract UI-applied filter chips from graph-generate / distance-matching / saved-filters.
 * Prefer `applied_filters` or `saved_filters.applied` — never `job_kw`.
 */
export const extractAppliedFiltersPayload = (raw: unknown): CachedCandidateFilters => {
  return normalizeCachedFiltersResponse(raw);
};

/** Count / slider metadata from graph-generate or distance-matching responses. */
export const extractGraphResponseMeta = (raw: unknown): GraphResponseMeta => {
  const { body } = extractGraphSeries(raw);
  if (!body || typeof body !== 'object') {
    return { sliderCandidateCount: null, filterSearchTotalCandidates: null, selectedPercentageFit: null };
  }

  const graph = (body.saved_filters as Record<string, unknown> | undefined)?.graph as
    | Record<string, unknown>
    | undefined;

  return {
    sliderCandidateCount: toNumberOrNull(body.slider_candidate_count ?? body.count_at_selected_fit),
    filterSearchTotalCandidates: toNumberOrNull(
      body.filter_search_total_candidates ?? graph?.total_at_min_fit,
    ),
    selectedPercentageFit: toNumberOrNull(
      body.selected_percentage_fit ?? graph?.selected_percentage_fit,
    ),
  };
};

/** Build generate-candidate payload from applied/cached snapshot (not localStorage). */
export const buildGenerateCandidatePayload = (
  jobId: string,
  percentageFit: number,
  cached: CachedCandidateFilters | null | undefined,
  options?: { semantic?: boolean },
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    job: jobId,
    percentage_fit: percentageFit,
    distance_in_miles: cached?.distance_in_miles ?? null,
  };

  if (options?.semantic !== undefined) {
    payload.semantic = options.semantic;
  }

  const cities = Array.isArray(cached?.cities) ? cached!.cities!.filter(Boolean) : [];
  if (cities.length > 0) {
    payload.cities = cities;
  }

  const filters = cached?.filters;
  if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {
    payload.filters = filters;
  }

  return payload;
};

/** Location fields for distance-matching / generate-candidate (prefer `cities` array). */
export const buildLocationFields = (locations: string[]): Record<string, unknown> => {
  const cleaned = locations.map((l) => String(l || '').trim()).filter(Boolean);
  if (cleaned.length === 0) return { cities: [] };
  return { cities: cleaned };
};
